import {
  DashboardState,
  ECardinality,
  ESdChangeReason,
  ESymbolCategory,
  ISymbol,
  IValidation
} from "@kuika/kuika-cl-model";
import { ConditionSetType } from "@kuika/kuika-cl-model/src/models/builder.dtos";
import { Guid } from "guid-typescript";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { PropertyHelper } from "../../../../shared/utility/property-helper";
import ActionSectionEmptyState from "./action-section-empty-state";
import { ActionSectionAddButton, ActionSectionTitle } from "./action-section-header";
import { ValidationPanel } from "./validation-panel";

export interface IValidationsState {}

export interface IValidationsProps {}

class Validations extends PureSdComponent<IValidationsProps & IWithSDProps & IWithContextProp, IValidationsState> {
  propertyHelper: PropertyHelper;

  constructor(props: IValidationsProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.propertyHelper = new PropertyHelper();
  }

  getSelectedDisplayItem = () => {
    const sd = this.props.sd.getScreenDesign();
    const selectedDisplayItem = this.props.sd.getSelectedDisplayItem(sd);
    return selectedDisplayItem;
  };

  getSelectedKuikaIds = () => {
    if (
      this.props.sd.dashboardState === DashboardState.dataDiagramScheme ||
      this.props.sd.dashboardState === DashboardState.workFlow
    ) {
      return ["-1"];
    }
    return this.props.sd.selectedKuikaIds;
  };

  handleUpdateValidation = (validation: IValidation, changeReason?: ESdChangeReason | undefined) => {
    const kuikaId = this.getSelectedKuikaIds()[0];
    this.props.sd.saveValidationToIValidation(kuikaId, validation, changeReason);
  };

  handleDeleteValidation = (validation: IValidation) => {
    const kuikaId = this.getSelectedKuikaIds()[0];
    this.props.sd.deleteValidateFromIValidation(kuikaId, validation);
  };

  addNewValidation = (): void => {
    const validationId = Guid.create();
    const sd = this.props.sd.getScreenDesign();
    const currentDI = this.props.sd.getSelectedDisplayItem(sd);
    const compDef = this.props.sd.project.componentSetObj.components?.find(
      (a) => currentDI && a.id === currentDI.item.id
    );
    // A deprecated value prop (LocationSearch's compound `value`, KOA-18575) is no longer part of
    // the component's contract, so "required" is built from the live value props only.
    const valueProps = compDef?.properties?.filter((x) => x.isValueProp === true && x.isDeprecated !== true) ?? [];
    const validation = {
      id: validationId.toString(),
      isExpandedPanel: true,
      message: "This field is required"
    } as IValidation;
    if (valueProps.length > 0) {
      // "required" means every value prop is set — one condition for a single-value component,
      // an AND over all of them for a multi-value one (DateRange, LocationPicker, LocationSearch).
      validation.condition = {
        id: Guid.create().toString(),
        conditionSet: {
          type: ConditionSetType.and,
          conditions: valueProps.map((valueProp) => {
            const valueType = this.propertyHelper.calculateBackEndValueType(valueProp);
            // Every condition carries an id: the condition editor makes each one a Draggable keyed by
            // it, and an id-less default condition broke its drag handle ("Draggable requires a
            // draggableId") — the editor's own "add condition" always assigns one.
            return {
              id: Guid.create().toString(),
              field1: {
                description: `[diId:${currentDI?.id}].${valueProp.name}`,
                category: ESymbolCategory.displayItemProp,
                label: "Property Of Component",
                diId: currentDI?.id,
                diPropName: valueProp.name,
                currentType: valueType,
                targetType: valueType,
                currentCardinality: ECardinality.One,
                targetCardinality: ECardinality.One
              } as ISymbol,
              conditionOperator: "isNotEmpty"
            };
          })
        }
      };
    }
    currentDI?.item?.validations?.push(validation);
    const kuikaId = this.getSelectedKuikaIds()[0];
    this.props.sd.saveValidationToIValidation(kuikaId, validation);
  };

  render() {
    if (!this.props || !this.props.sd) {
      return <></>;
    }
    const selectedDisplayItem = this.getSelectedDisplayItem();
    if (!selectedDisplayItem) {
      return <></>;
    }
    const validations: IValidation[] = selectedDisplayItem.item?.validations ?? [];
    return (
      <>
        <div className="kuika_Wrapper">
          {/* The same section chrome as Actions and Screen Inputs: title + count, the "+" at the right
              edge, and the dashed empty card while there is nothing to list. */}
          <div className="kuika_actionPanelWrapperHeader kuika-action-section__header">
            <div className="kuika-action-section__header-row">
              <ActionSectionTitle title="Validations" count={validations.length} />
              <span className="kuika-action-section__add-slot">
                <ActionSectionAddButton
                  ariaLabel="Add validation"
                  testId="addValidationButton"
                  onClick={this.addNewValidation}
                />
              </span>
            </div>
          </div>
          {validations.length === 0 ? (
            <ActionSectionEmptyState
              message="No validations yet."
              actionLabel="Add validation"
              onAdd={this.addNewValidation}
              testId="validationsEmptyState"
            />
          ) : (
            validations.map((validation: IValidation): JSX.Element => (
              <div key={validation.id}>
                <ValidationPanel
                  validation={validation}
                  onUpdateValidation={this.handleUpdateValidation}
                  onDeleteValidation={this.handleDeleteValidation}
                />
              </div>
            ))
          )}
        </div>
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.UpdateValidation ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock
    ) {
      return true;
    }
    return false;
  };
}

const validations = withContext<IValidationsProps>(withSD<IValidationsProps>(Validations));
export { validations as Validations };
